import { Divider, Stack, ThemeProvider, Typography } from '@mui/material'
import { TypographyVariant } from '@mui/material/styles/createTypography'
import { UserV1UserServiceowner } from 'c1gen/roles'
import { maxTooltipWidth } from 'c1ui'
import { C1CircularProgress } from 'c1ui/components/C1CircularProgress'
import { C1DarkTheme } from 'c1ui/theme/C1Theme'
import { NoUserAvatar, UserAvatar } from 'components/core/avatar'
import { AvatarSize } from 'components/core/avatar/AvatarSizes'
import { C1LinkButton } from 'components/core/button/C1LinkButton'
import { C1DisplayNameSkeleton, DisplayNameDetails } from 'components/core/display-name-details'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { MinimalAPIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { Metadata, MetadataPropType } from 'components/core/object-display/C1ObjectDisplay'
import { MetadataKeyValuePair } from 'components/core/page-header'
import { VariableText } from 'components/core/variable-text'
import { useHasRole } from 'components/no-ui/capability'
import { userStatusToIdentifier } from 'components/pages/user/type/index'
import { UserStatusDisplay } from 'components/pages/user/user_status'
import { UserValue } from 'lib/actions/user'
import { routes, Route } from 'lib/routes'
import { ExternalLink } from 'lucide-react'
import { UserRef, UserStatus, UserType } from 'pbts/c1/api/user/v1/user'
import { FC, ReactNode, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { getUserMultiApi } from 'redux_toolkit/users'

export const UnknownUserID = 'unknown_user_id'

type UserTooltipContentWithFetchProps = {
    userId: string
}

export const UserTooltipContentWithFetch: FC<UserTooltipContentWithFetchProps> = ({ userId }) => {
    const userRef = useMemo(() => UserRef.create({ id: userId }), [userId])

    const { data: user, isLoading, isError, error } = getUserMultiApi.useMultiGet(userRef)

    if (isLoading) {
        return <C1CircularProgress />
    }

    if (isError || !user) {
        return <MinimalAPIError err={error?.message || 'Error fetching user'} />
    }

    return <UserTooltipContent user={user} />
}

type UserTooltipContentProps = {
    user: UserValue
}

export const UserTooltipContent: FC<UserTooltipContentProps> = ({ user }) => {
    const intl = useIntl()

    return <ThemeProvider theme={C1DarkTheme}>
        <Stack gap={1} py={1} sx={{ maxWidth: maxTooltipWidth }}>
            <Stack direction="row" gap={2} mb={1}>
                <Typography variant="h4">{user.displayName}</Typography>
                <UserStatusDisplay status={user.status} />
            </Stack>

            <MetadataKeyValuePair
                metadataKey={intl.formatMessage({
                    id: 'component_user_display_name_email',
                    defaultMessage: 'Email',
                })}
                metadataValue={user.email || <C1EmptyIndicator />}
                isCopyable
                copyValue={user.email}
                fullWidth
            />

            <MetadataKeyValuePair
                metadataKey={intl.formatMessage({
                    id: 'component_user_display_name_roles',
                    defaultMessage: 'Roles',
                })}
                metadataValue={
                    <VariableText
                        noWrap
                        variableText={user.roles?.map((r) => r.displayName).join(', ')}
                        emptyState={intl.formatMessage({
                            id: 'components_user_details_no_roles',
                            defaultMessage: 'No roles found',
                        })}
                    />
                }
                isCopyable
                copyValue={user.roles?.map((r) => r.displayName).join(', ') || ''}
                fullWidth
            />

            <MetadataKeyValuePair
                metadataKey={intl.formatMessage({
                    id: 'component_user_display_name_job_title',
                    defaultMessage: 'Job title',
                })}
                metadataValue={<VariableText variableText={user.jobTitle} />}
                isCopyable
                copyValue={user.jobTitle}
                fullWidth
            />

            <MetadataKeyValuePair
                metadataKey={intl.formatMessage({
                    id: 'component_user_display_name_department',
                    defaultMessage: 'Department',
                })}
                metadataValue={<VariableText variableText={user.department} />}
                isCopyable
                copyValue={user.department}
                fullWidth
            />

            <MetadataKeyValuePair
                metadataKey={intl.formatMessage({
                    id: 'component_user_display_name_employment_status',
                    defaultMessage: 'Employment status',
                })}
                metadataValue={<VariableText variableText={user.employmentStatus} />}
                isCopyable
                copyValue={user.employmentStatus}
                fullWidth
            />

            <MetadataKeyValuePair
                metadataKey={intl.formatMessage({
                    id: 'component_user_display_name_employment_type',
                    defaultMessage: 'Employment type',
                })}
                metadataValue={<VariableText variableText={user.employmentType} />}
                isCopyable
                copyValue={user.employmentType}
                fullWidth
            />

            <Divider sx={{ mb: 1, mt: 2, borderColor: 'divider' }} />

            <C1LinkButton
                href={routes.users.detail(user.id)}
                target="_blank"
                color="info"
                variant="text"
                endIcon={<C1Icon icon={ExternalLink} />}
            >
                <FormattedMessage id="component_app_tooltip_view_app_details" defaultMessage="View user detail page" />
            </C1LinkButton>
        </Stack>
    </ThemeProvider>
}

const UserNameMetadata: FC<{
    user: Record<'jobTitle' | 'department' | 'email', string>
    includeDetails: boolean
    iconOnly: boolean
}> = ({ user, includeDetails, iconOnly }): React.ReactElement | null => {
    const metadata = useMemo(() => {
        const result: MetadataPropType = []

        if (user.email && result.length < 2) result.push({ text: user.email })
        if (user.jobTitle && result.length < 2) result.push({ text: user.jobTitle })
        if (user.department && result.length < 2) result.push({ text: user.department })

        return result
    }, [user])

    if (iconOnly || !includeDetails) return null

    return <Metadata metadata={metadata} />
}

type UserDisplayNameProps = {
    user?: UserValue
    avatarSize?: AvatarSize
    readOnly?: boolean
    displayNameVariant?: TypographyVariant
    includeDetails?: boolean
    showTooltip?: boolean
    padded?: boolean
    margin?: boolean
    showAvatar?: boolean
    showUserStatus?: boolean
    iconOnly?: boolean
    maxWidth?: string
    avatarIsEditable?: boolean
}

export const getUserLink = (user: UserValue, readOnly: boolean): Route | undefined => {
    if (readOnly) {
        return undefined
    }

    if (user.type === UserType.AGENT) {
        return routes.settings.agenticAi.agent(user.id)
    }

    return routes.users.detail(user.id)
}

export const UserDisplayName: FC<UserDisplayNameProps> = ({
    user,
    avatarSize = 'md',
    showAvatar = true,
    readOnly = false,
    displayNameVariant,
    includeDetails = true,
    showTooltip = false,
    padded = false,
    showUserStatus = false,
    margin,
    iconOnly = false,
    maxWidth,
    avatarIsEditable = false,
}) => {
    const intl = useIntl()
    const isOwner = useHasRole(UserV1UserServiceowner)

    if (!user) {
        return null
    }

    const avatar = showAvatar
        ? <UserAvatar
            user={user}
            readOnly={readOnly}
            size={avatarSize}
            editable={avatarIsEditable}
            decorative={!iconOnly}
        />
        : undefined

    const link = getUserLink(user, readOnly)

    const tooltipContent = <UserTooltipContent user={user} />

    const displayName = user.displayName || user.email

    return (
        <DisplayNameDetails
            avatar={avatar}
            displayName={!iconOnly ? displayName : ''}
            link={link}
            details={<UserNameMetadata user={user} includeDetails={includeDetails} iconOnly={iconOnly} />}
            readOnly={readOnly}
            displayNameVariant={displayNameVariant}
            tooltipContent={user.type === UserType.AGENT ? undefined : tooltipContent} // No tooltip for agents
            showTooltip={showTooltip && isOwner}
            padded={padded}
            margin={margin}
            identifier={showUserStatus && user.status !== UserStatus.ENABLED ? userStatusToIdentifier(user.status, intl) : undefined}
            fullWidth={false}
            maxWidth={maxWidth}
            tooltipProps={{
                PopperProps: {
                    sx: {
                        '& .MuiTooltip-tooltip': { maxWidth: 'none' },
                    },
                },
                slotProps: {},
            }}
            actionName="View user"
        />
    )
}

export const UnknownUserDisplay: FC<{ avatarSize?: AvatarSize }> = ({ avatarSize }) => {
    const intl = useIntl()

    return (
        <DisplayNameDetails
            avatar={
                <NoUserAvatar size={avatarSize} />
            }
            displayName={intl.formatMessage(
                {
                    id: 'task_rollup_account_owner_unassigned_display_name',
                    defaultMessage: 'Ownerless accounts',
                },
            )}
            showTooltip={false}
            readOnly
            details={
                <Metadata
                    metadata={[
                        {
                            text: intl.formatMessage({
                                id: 'task_rollup_account_owner_unassigned_metadata_text',
                                defaultMessage: 'Accounts not associated with a user',
                            }),
                        },
                    ]}
                />
            }
        />
    )
}

// Fetches the app user and displays the app user display name.
type UserDisplayNameWithFetchProps = {
    id: string
} & Omit<UserDisplayNameProps, 'user'>

export const UserDisplayNameWithFetch = ({ id, ...rest }: UserDisplayNameWithFetchProps): ReactNode => {
    const userRef = useMemo(() => UserRef.create({ id }), [id])

    const {
        data: user,
        isLoading: userLoading,
        isError: isUserError,
        error: userError,
    } = getUserMultiApi.useMultiGet(userRef)

    if (isUserError && userError) {
        return <MinimalAPIError err={userError.message} />
    }

    if (!user) {
        return userLoading ? <C1DisplayNameSkeleton /> : null
    }

    return <UserDisplayName user={user} {...rest} />
}
